/**
 * Own Residence Form Component
 * Reusable form for own-specific fields
 */

import { DatePicker } from '@components/ui/DatePicker'
import { FormInput, FormField } from '@components/ui/FormField'
import { formatDateForAPI, formatDateForDisplay } from '@/features/applicant/utils/addressHelpers'
import { convertToDateInputFormat } from '@/lib/utils/dateValidation'
import { formatCurrencyInput, getNumericCurrencyValue } from '@/lib/utils/formatters'
import { APPLICANT_CONTENT } from '@/features/applicant/constants/applicantContent'
import type { OwnFormErrors } from './types'

interface OwnFormProps {
  // Form values
  moveInDate: string
  plannedMoveOutDate: string
  monthlyMortgagePayment: string
  monthsResided: string
  stillHere: boolean

  // Form handlers
  onMoveInDateChange: (value: string) => void
  onPlannedMoveOutDateChange: (value: string) => void
  onMonthlyMortgagePaymentChange: (value: string) => void
  onMonthsResidedChange: (value: string) => void
  onStillHereChange: (value: boolean) => void

  // Validation errors
  errors?: OwnFormErrors
  isReadOnly?: boolean
  isPreviousAddress?: boolean
}

export function OwnForm({
  moveInDate,
  plannedMoveOutDate,
  monthlyMortgagePayment,
  monthsResided,
  stillHere,
  onMoveInDateChange,
  onPlannedMoveOutDateChange,
  onMonthlyMortgagePaymentChange,
  onMonthsResidedChange,
  onStillHereChange,
  errors = {},
  isReadOnly = false,
  isPreviousAddress = false,
}: OwnFormProps) {
  return (
    <div className="border-l-4 border-blue-500 bg-gradient-to-r from-blue-50 to-indigo-50 p-6 mb-6 rounded-r-lg shadow-sm">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {APPLICANT_CONTENT.application.sections.currentAddress.rentFields.moveInDate}*
          </label>
          <DatePicker
            value={moveInDate ? convertToDateInputFormat(moveInDate) : ''}
            onChange={(value) => {
              const displayValue = formatDateForDisplay(value)
              onMoveInDateChange(displayValue)
            }}
            placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.dateOfBirth}
            minDate={new Date(1900, 0, 1)}
            maxDate={
              plannedMoveOutDate
                ? new Date(Math.min(new Date().getTime(), new Date(formatDateForAPI(plannedMoveOutDate)).getTime()))
                : new Date()
            }
            showHelperText={false}
            error={errors.moveInDate}
            disabled={isReadOnly}
          />
        </div>
        {!isPreviousAddress && (
          <div>
            <div className="flex items-center gap-4 mb-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={stillHere}
                  onChange={(e) => {
                    if (!isReadOnly) {
                      onStillHereChange(e.target.checked)
                      if (e.target.checked) {
                        onPlannedMoveOutDateChange('')
                      }
                    }
                  }}
                  disabled={isReadOnly}
                  className="mr-2 w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                />
                <span className="text-sm text-gray-700">{APPLICANT_CONTENT.application.sections.currentAddress.rentFields.stillHere}</span>
              </label>
              <span className="text-sm text-gray-700">{APPLICANT_CONTENT.application.sections.currentAddress.ownFields.plannedMoveOutDate}</span>
            </div>
            <DatePicker
              value={plannedMoveOutDate ? convertToDateInputFormat(plannedMoveOutDate) : ''}
              onChange={(value) => {
                const displayValue = formatDateForDisplay(value)
                onPlannedMoveOutDateChange(displayValue)
              }}
              placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.dateOfBirth}
              minDate={(() => {
                const today = new Date()
                today.setHours(0, 0, 0, 0)
                if (moveInDate) {
                  const moveIn = new Date(formatDateForAPI(moveInDate))
                  return moveIn > today ? moveIn : today
                }
                return today
              })()}
              allowFutureDates={true}
              disabled={isReadOnly || stillHere}
              showHelperText={false}
              error={errors.plannedMoveOutDate}
            />
          </div>
        )}
        {isPreviousAddress ? (
          <FormField
            label={APPLICANT_CONTENT.application.sections.currentAddress.ownFields.monthsResided}
            required
          >
            <FormInput
              type="text"
              value={monthsResided || '0'}
              onChange={(e) => onMonthsResidedChange(e.target.value)}
              placeholder="Resided Months"
              error={errors.monthsResided}
              disabled={isReadOnly}
            />
          </FormField>
        ) : (
          <FormField
            label={APPLICANT_CONTENT.application.sections.currentAddress.ownFields.monthlyMortgagePayment}
            required
          >
            <FormInput
              type="text"
              value={monthlyMortgagePayment ? formatCurrencyInput(monthlyMortgagePayment) : ''}
              onChange={(e) => {
                // Extract numeric value from formatted input
                const numericValue = getNumericCurrencyValue(e.target.value)
                // Store only the numeric value
                onMonthlyMortgagePaymentChange(numericValue)
              }}
              onBlur={() => {
                // On blur, strip any stray formatting characters so state stays numeric
                if (monthlyMortgagePayment) {
                  const numeric = getNumericCurrencyValue(monthlyMortgagePayment)
                  if (numeric !== monthlyMortgagePayment) {
                    onMonthlyMortgagePaymentChange(numeric)
                  }
                }
              }}
              placeholder="$0.00"
              error={errors.monthlyMortgagePayment}
              disabled={isReadOnly}
            />
          </FormField>
        )}
      </div>
    </div>
  )
}

